import React, { useEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function VideoSwipe({
  videos,
  initialIndex = 0,
  loop = false,
  className = "",
  style = {},
  ratio = "9 / 16", // vertical tipo reels
  background = "#fff", // para quitar franjas negras
}) {
  // Si no te pasan videos, usa los locales por defecto (mismo folder)
  const defaultVideos = useMemo(
    () => [
      require("./v1.mp4"),
      require("./v2.mp4"),
      require("./v3.mp4"),
      require("./v4.mp4"),
      require("./v5.mp4"),
      require("./v6.mp4"),
      require("./v7.mp4"),
    ],
    [],
  );

  const list = videos?.length ? videos : defaultVideos;

  const videoRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(false);

  // Pausa todos menos el activo
  const pauseAllExcept = (keepIdx) => {
    videoRefs.current.forEach((vid, i) => {
      if (!vid) return;
      if (i !== keepIdx) {
        try {
          vid.pause();
          vid.currentTime = 0;
        } catch (_) {}
      }
    });
  };

  // Cuando cambias de slide, pausamos todo y dejamos el activo en pausa
  useEffect(() => {
    pauseAllExcept(activeIndex);
    setIsPlaying(false);
  }, [activeIndex]);

  const onSlideChange = (swiper) => {
    const idx =
      typeof swiper.realIndex === "number"
        ? swiper.realIndex
        : swiper.activeIndex;
    setActiveIndex(idx);
  };

  const togglePlay = async () => {
    const vid = videoRefs.current[activeIndex];
    if (!vid) return;

    try {
      if (vid.paused) {
        pauseAllExcept(activeIndex);
        await vid.play();
        setIsPlaying(true);
      } else {
        vid.pause();
        setIsPlaying(false);
      }
    } catch (e) {
      // autoplay policies / gestures raras, etc.
      // igual no lo tronamos
      setIsPlaying(!vid?.paused);
    }
  };

  return (
    <div id="videos" className={className} style={{ width: "100%", ...style }}>
      <Swiper
        modules={[Navigation, Pagination, Keyboard]}
        navigation
        pagination={{ clickable: true }}
        keyboard={{ enabled: true }}
        loop={loop}
        initialSlide={initialIndex}
        onSlideChange={onSlideChange}
        spaceBetween={16}
        slidesPerView={1}
        style={{ paddingBottom: 30 }}
        // Evita loops de ResizeObserver por cambios de tamaño
        observer={false}
        observeParents={false}
        resizeObserver={false}
        updateOnWindowResize
      >
        {list.map((src, idx) => (
          <SwiperSlide key={idx}>
            <div
              style={{
                width: "100%",
                maxWidth: 520, // opcional: no lo dejes enorme
                margin: "0 auto",
                aspectRatio: ratio,
                borderRadius: 18,
                overflow: "hidden",
                background,
                position: "relative",
                touchAction: "pan-y", // importante: deja swipe horizontal al Swiper
                userSelect: "none",
              }}
            >
              <video
                ref={(el) => (videoRefs.current[idx] = el)}
                src={src}
                playsInline
                preload="metadata"
                controls={false} // 👈 para que el swipe no se pelee con los controles nativos
                onPlay={() => idx === activeIndex && setIsPlaying(true)}
                onPause={() => idx === activeIndex && setIsPlaying(false)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain", // no recorta; cambia a "cover" si quieres fill
                  display: "block",
                  background: "transparent",
                }}
              />

              {/* Overlay clickeable (tap) para play/pause */}
              <button
                type="button"
                onClick={togglePlay}
                aria-label={isPlaying ? "Pausar video" : "Reproducir video"}
                style={{
                  position: "absolute",
                  inset: 0,
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {/* Ícono Play/Pause en el centro */}
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 999,
                    background: "rgba(0,0,0,0.35)",
                    display: "grid",
                    placeItems: "center",
                    backdropFilter: "blur(2px)",
                    transform: isPlaying ? "scale(0.85)" : "scale(1)",
                    opacity: isPlaying ? 0 : 1, // cuando está reproduciendo, se oculta
                    transition: "opacity 180ms ease, transform 180ms ease",
                    pointerEvents: "none", // el click lo maneja el botón
                  }}
                >
                  {isPlaying ? (
                    // Pause (no se verá por opacity 0, pero lo dejo listo)
                    <div style={{ display: "flex", gap: 10 }}>
                      <span
                        style={{
                          width: 10,
                          height: 28,
                          background: "#fff",
                          borderRadius: 2,
                        }}
                      />
                      <span
                        style={{
                          width: 10,
                          height: 28,
                          background: "#fff",
                          borderRadius: 2,
                        }}
                      />
                    </div>
                  ) : (
                    // Play
                    <div
                      style={{
                        width: 0,
                        height: 0,
                        borderTop: "14px solid transparent",
                        borderBottom: "14px solid transparent",
                        borderLeft: "22px solid #fff",
                        marginLeft: 6,
                      }}
                    />
                  )}
                </div>
              </button>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
